import type { ReactNode } from "react";

import type { HarmonicAssessmentResponse } from "../services/harmonics";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";
import { COMPARISON_LABELS, DEMAND_SOURCE_LABELS, LIMIT_BASIS_LABELS } from "./harmonicsLabels";
import { SHORT_CIRCUIT_SOURCE_LABELS } from "./pfCorrectionLabels";

interface HarmonicResultPanelProps {
  result: HarmonicAssessmentResponse;
  /** The engine that produced a stored run; names the engine behind a field it did not record. */
  engineVersion?: string;
}

// The words of a comparison outcome in the Result column: WITHIN passes, ABOVE fails the
// limit, NOT_EVALUATED was not made (no figure, or no limit).
function comparisonKey(comparison: HarmonicAssessmentResponse["tdd_comparison"]): string {
  switch (comparison) {
    case "WITHIN":
      return "pass";
    case "ABOVE":
      return "fail";
    default:
      return "not-evaluated";
  }
}

function Quantity({ value, unit }: { value: string | null | undefined; unit: string }) {
  const quantity = formatQuantity(value ?? null, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function Row({ name, label, children }: { name: string; label: string; children: ReactNode }) {
  return (
    <tr data-row-key={name}>
      <th scope="row">{label}</th>
      {children}
    </tr>
  );
}

function QuantityRow({ name, label, value, unit }: { name: string; label: string; value: string | null | undefined; unit: string }) {
  return (
    <Row name={name} label={label}>
      <Quantity value={value} unit={unit} />
    </Row>
  );
}

function TextRow({ name, label, value }: { name: string; label: string; value: ReactNode }) {
  return (
    <Row name={name} label={label}>
      <td>{value ?? EMPTY_QUANTITY}</td>
    </Row>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

// The two comparisons (D2): each quantity against its limit, with the basis the limit stands on.
function ChecksTable({ result }: { result: HarmonicAssessmentResponse }) {
  const checks = [
    {
      key: "tdd",
      label: "TDD against the limit",
      value: result.tdd_percent,
      limit: result.tdd_limit_percent,
      comparison: result.tdd_comparison,
    },
    {
      key: "thdv",
      label: "THDv against the limit",
      value: result.thdv_percent,
      limit: result.thdv_limit_percent,
      comparison: result.thdv_comparison,
    },
  ];
  return (
    <section aria-label="Checks">
      <h3>Checks</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Check</th>
              <th scope="col">Value</th>
              <th scope="col">Limit</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((item) => {
              const key = comparisonKey(item.comparison);
              return (
                <tr key={item.key} data-check-key={item.key}>
                  <th scope="row">{item.label}</th>
                  <Quantity value={item.value} unit="%" />
                  <Quantity value={item.limit} unit="%" />
                  <td data-check-result={key}>
                    <StatusIcon tone={checkTone(key)} size={16} />
                    {COMPARISON_LABELS[item.comparison]}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p data-limit-basis={result.limit_basis}>
        Limit basis: {LIMIT_BASIS_LABELS[result.limit_basis]}
        {result.limit_source !== null ? ` · ${result.limit_source}` : ""}
      </p>
    </section>
  );
}

// The declared loads as the engine saw them: the spectrum in amperes (a percent declaration
// resolved), each with its source.
function LoadsTable({ result }: { result: HarmonicAssessmentResponse }) {
  return (
    <section aria-label="Distorting loads">
      <h3>Distorting loads</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Load</th>
              <th scope="col">I1 (A)</th>
              <th scope="col">Spectrum (order: A)</th>
              <th scope="col">Source</th>
            </tr>
          </thead>
          <tbody>
            {result.loads.map((load) => {
              const i1 = formatQuantity(load.fundamental_current_a, "A");
              return (
                <tr key={load.code} data-load-code={load.code}>
                  <th scope="row">{load.code}</th>
                  <td title={i1.exact ?? undefined}>{i1.display}</td>
                  <td>
                    {load.spectrum.length > 0
                      ? load.spectrum.map((c) => `${c.order}: ${c.current_a}`).join(" · ")
                      : EMPTY_QUANTITY}
                  </td>
                  <td>
                    {load.spectrum_source ?? EMPTY_QUANTITY}
                    {load.spectrum_source_date !== null ? ` (${load.spectrum_source_date})` : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// The per-order sheet: the summed current per order and the voltage it drives at the PCC.
function OrdersTable({ result }: { result: HarmonicAssessmentResponse }) {
  return (
    <section aria-label="Harmonic orders">
      <h3>Harmonic orders</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Order</th>
              <th scope="col">Current (A)</th>
              <th scope="col">Voltage (V)</th>
              <th scope="col">Voltage (% of U1)</th>
            </tr>
          </thead>
          <tbody>
            {result.orders.map((order) => (
              <tr key={order.order} data-harmonic-order={order.order}>
                <th scope="row">{order.order}</th>
                <Quantity value={order.current_a} unit="A" />
                <Quantity value={order.voltage_v} unit="V" />
                <Quantity value={order.voltage_percent} unit="%" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// The demand current and the short-circuit power with where each came from, the totals and
// the Isc basis (section 3, items 6 to 8).
function BasisTable({ result }: { result: HarmonicAssessmentResponse }) {
  const linked = result.linked_load_run;
  const power = result.short_circuit_power;
  const linkedFault = power !== null && power.source === "LINKED_FAULT_RUN";
  return (
    <ValueTable title="Distortion figures and their basis">
      <QuantityRow name="fundamental_current_a" label="Fundamental current of the loads (A)" value={result.fundamental_current_a} unit="A" />
      <QuantityRow name="harmonic_current_rms_a" label="Harmonic current rms (A)" value={result.harmonic_current_rms_a} unit="A" />
      <QuantityRow name="thdi_percent" label="THDi (%)" value={result.thdi_percent} unit="%" />
      <TextRow name="demand_current_source" label="Demand current source" value={result.demand_current_source ? DEMAND_SOURCE_LABELS[result.demand_current_source] : EMPTY_QUANTITY} />
      <TextRow name="linked_load_run" label="Load run" value={linked ? `${linked.group_code} rev ${linked.revision_number}` : EMPTY_QUANTITY} />
      <TextRow name="demand_current_source_text" label="Demand current basis" value={result.demand_current_source_text ?? EMPTY_QUANTITY} />
      <QuantityRow name="maximum_demand_current_a" label="Maximum demand current IL (A)" value={result.maximum_demand_current_a} unit="A" />
      <QuantityRow name="tdd_percent" label="TDD (%)" value={result.tdd_percent} unit="%" />
      <TextRow name="short_circuit_power.source" label="Short-circuit power source" value={power ? SHORT_CIRCUIT_SOURCE_LABELS[power.source] : EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.fault_run_id" label="Fault run" value={power?.fault_run_id ?? EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.fault_bus_code" label="Fault bus" value={power?.fault_bus_code ?? EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.calculation_case" label="Calculation case" value={linkedFault && power.calculation_case ? CASE_LABELS[power.calculation_case] : EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.fault_type" label="Fault type" value={linkedFault && power.fault_type ? FAULT_TYPE_LABELS[power.fault_type] : EMPTY_QUANTITY} />
      <QuantityRow name="short_circuit_power.value_kva" label="Short-circuit power S_sc (kVA)" value={power?.value_kva} unit="kVA" />
      <TextRow name="short_circuit_power.basis" label="S_sc basis" value={power?.basis ?? EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.source_text" label="Source" value={power?.source_text ?? EMPTY_QUANTITY} />
      <QuantityRow name="short_circuit_power.short_circuit_current_ka" label="Ik'' of the run (kA)" value={power?.short_circuit_current_ka} unit="kA" />
      <QuantityRow name="thdv_percent" label="THDv (%)" value={result.thdv_percent} unit="%" />
      <TextRow name="isc_il_ratio" label="Isc / IL" value={result.isc_il_ratio ?? EMPTY_QUANTITY} />
      <TextRow name="isc_basis" label="Isc basis" value={result.isc_basis ?? EMPTY_QUANTITY} />
      <QuantityRow name="system_nominal_voltage_v" label="System nominal voltage (V)" value={result.system_nominal_voltage_v} unit="V" />
      <QuantityRow name="frequency_hz" label="Frequency (Hz)" value={result.frequency_hz} unit="Hz" />
    </ValueTable>
  );
}

// The detail tables of one harmonic assessment (Master Prompt v2.1 section 19): the two
// comparisons first, then the loads, the per-order sheet, the figures with their basis, and
// the fixed method note the engine carries. Nothing is recomputed here.
export function HarmonicResultPanel({ result }: HarmonicResultPanelProps) {
  return (
    <section aria-label="Result detail" data-result-status={result.status}>
      <ChecksTable result={result} />
      <LoadsTable result={result} />
      <OrdersTable result={result} />
      <BasisTable result={result} />
      <p data-method-note="true">{result.method_note}</p>
    </section>
  );
}

export default HarmonicResultPanel;
